import { dayjs } from "@thalamos/common";
import { MhaStatus } from "../types/patientState/PatientState.js";
export function isFirstDayOfMonth(utcString) {
    return (dayjs.utc(utcString).tz("Europe/London").startOf("day").format("DD") ===
        "01");
}
export function startOfDayUkTime(timestamp) {
    return dayjs
        .utc(timestamp)
        .tz("Europe/London")
        .startOf("day")
        .utc()
        .toISOString();
}
// Returns the date defined legally as the end of a 6 month
// MHA detention period
export function addDurationToStartDate(startDate, duration, zone = "Europe/London") {
    return dayjs
        .utc(startDate)
        .tz(zone)
        .add(duration)
        .subtract(1, "day")
        .endOf("day")
        .millisecond(0)
        .utc();
}
export function getRenewalDates(initialStartDate, numberOfRenewals) {
    if (initialStartDate && dayjs(initialStartDate).isValid()) {
        const startOfDay = startOfDayUkTime(initialStartDate);
        const originalRenewalEndDate = calculateSection3OrCtoEndDate(startOfDay, 0);
        const currentRenewalStartDate = dayjs
            .utc(calculateSection3OrCtoEndDate(startOfDay, numberOfRenewals - 1))
            .add(1, "seconds")
            .toISOString();
        const currentRenewalEndDate = calculateSection3OrCtoEndDate(startOfDay, numberOfRenewals);
        return {
            originalRenewalEndDate,
            currentRenewalStartDate,
            currentRenewalEndDate,
        };
    }
    return {
        originalRenewalEndDate: undefined,
        currentRenewalStartDate: undefined,
        currentRenewalEndDate: undefined,
    };
}
/*
  Initial period 6 months. A section 3 / cto can be renewed after 6 months.
  After that, it can be renewed every 12 months.
*/
export function calculateSection3OrCtoEndDate(initialStartDate, numberOfRenewals) {
    let numberOfMonthsToAdd;
    switch (numberOfRenewals) {
        case 0:
            numberOfMonthsToAdd = 6;
            break;
        case 1:
            numberOfMonthsToAdd = 12;
            break;
        default:
            numberOfMonthsToAdd = 12 + (numberOfRenewals - 1) * 12;
            break;
    }
    // Subtract a second to make the end date exclusive
    return addDurationToStartDate(initialStartDate, dayjs.duration(numberOfMonthsToAdd, "months")).toISOString();
}
export function getSection2ExpiryDate(effectiveDateTime) {
    const section2StartDateTime = startOfDayUkTime(effectiveDateTime);
    return addDurationToStartDate(section2StartDateTime, dayjs.duration(28, "days")).toISOString();
}
export function getExpiryDate({ MhaStatusType, effectiveDateTime, numberOfRenewals, }) {
    // Every period below is measured from the detention's start, so without one
    // there is no expiry to report. Callers must still reject an unparseable
    // start themselves — dayjs throws on those rather than returning undefined.
    if (effectiveDateTime === undefined) {
        return undefined;
    }
    switch (MhaStatusType) {
        case MhaStatus.Section5_4:
            return dayjs(effectiveDateTime).add(6, "hours").toISOString();
        case MhaStatus.Section5_2:
        case MhaStatus.Section4:
            return dayjs(effectiveDateTime).add(72, "hours").toISOString();
        case MhaStatus.Section2:
            return getSection2ExpiryDate(effectiveDateTime);
        case MhaStatus.Section3:
        case MhaStatus.Section17A: {
            // A detention with no renewals recorded is still in its initial period,
            // which is what a renewal count of zero means.
            const { currentRenewalEndDate } = getRenewalDates(effectiveDateTime, numberOfRenewals ?? 0);
            return currentRenewalEndDate;
        }
        case MhaStatus.Section136:
            return dayjs(effectiveDateTime).add(24, "hours").toISOString();
    }
}
export function isValidStartDateForManualEvent(manualEvent) {
    const { status } = manualEvent;
    if (status === MhaStatus.Section3 || status === MhaStatus.Section17A) {
        // startDateTime is optional but if it is set should be in the past
        if (manualEvent.startDateTime) {
            if (manualEvent.numberOfRenewals && manualEvent.numberOfRenewals > 0) {
                // Section 3 / CTO with pending renewal
                return dayjs(manualEvent.startDateTime).isBefore(dayjs().add(2, "months"));
            }
            else {
                // Pending CTO
                if (status === MhaStatus.Section17A) {
                    return dayjs(manualEvent.startDateTime).isBefore(dayjs().add(2, "months"));
                }
                else {
                    return dayjs(manualEvent.startDateTime).isBefore(dayjs());
                }
            }
        }
    }
    else if (status === MhaStatus.Section5_4 ||
        status === MhaStatus.Section5_2 ||
        status === MhaStatus.Section4 ||
        status === MhaStatus.Section2) {
        // startDateTime is required and should be in the past
        return dayjs(manualEvent.startDateTime).isBefore(dayjs());
    }
    return true;
}
export function getActiveMhaStatus(state, asOfEffectiveTime) {
    // These states have no start or expiry date
    if ([
        MhaStatus.Unknown,
        MhaStatus.Expired,
        MhaStatus.Incomplete,
        MhaStatus.NotDetained,
    ].includes(state.mhaStatus.status))
        return { state: state.mhaStatus.status, type: "active" };
    if (state.mhaStatus.startDateTime && state.mhaStatus.expiryDateTime) {
        // Section in the future (e.g. renewed CTO or section 3 or a pending CTO)
        if (dayjs(state.mhaStatus.startDateTime).isAfter(dayjs(asOfEffectiveTime))) {
            // Renewed Section 3 or CTO which has yet to come into effect
            if ([MhaStatus.Section17A, MhaStatus.Section3].includes(state.mhaStatus.status) &&
                state.mhaStatus.numberOfRenewals &&
                state.mhaStatus.numberOfRenewals > 0) {
                return { state: state.mhaStatus.status, type: "pending-renewal" };
            }
            // Pending CTO means the patient is currently on a Section 3
            if (state.mhaStatus.status === MhaStatus.Section17A &&
                state.mhaStatus.numberOfRenewals === 0) {
                return { state: MhaStatus.Section3, type: "pending-cto" };
            }
        }
        // Currently in-effect section
        if (dayjs(state.mhaStatus.startDateTime).isBefore(dayjs(asOfEffectiveTime)) &&
            dayjs(state.mhaStatus.expiryDateTime).isAfter(dayjs(asOfEffectiveTime))) {
            return { state: state.mhaStatus.status, type: "active" };
        }
    }
    return { state: MhaStatus.Unknown, type: "active" };
}
//# sourceMappingURL=patientState.js.map